"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Droplet, Music, Mic, Truck, Shirt, Sparkles, Scissors, Flower2, Gem,
  Layers, Utensils, ChefHat, Drum, Milk, Coffee, UtensilsCrossed, Beef,
  SprayCan, Cake, Sprout, Fish, Search, ChevronLeft, ChevronRight, X, Crown, ArrowDown,
  type LucideIcon,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export interface Produit {
  num: number;
  file: string;
  name: string;
  url: string;
}

export interface Gamme {
  id: string;
  tag: string;
  name: string;
  desc: string;
  icon: string;
  count: number;
  produits: Produit[];
}

const ICONS: Record<string, LucideIcon> = {
  droplet: Droplet, music: Music, mic: Mic, truck: Truck, shirt: Shirt,
  sparkles: Sparkles, scissors: Scissors, flower: Flower2, gem: Gem,
  layers: Layers, utensils: Utensils, chef: ChefHat, drum: Drum,
  milk: Milk, coffee: Coffee, "utensils-crossed": UtensilsCrossed,
  beef: Beef, spray: SprayCan, cake: Cake, seed: Sprout, tea: Coffee, fish: Fish,
};

const SITES = [
  "adjiakhadybara.ligue-islamique.biz", "anta-khalifa.fr",
  "adjiafatimandiaye.ligue-islamique.biz", "adjiafatoumatademe.ligue-islamique.biz",
  "anta-khalifa.com", "seydinamuhammadeditions.com",
  "Al-Maoun.online", "papendiaw.ndiaye.info", "milliards.ndiaye.info",
];

const sansAccents = (s: string) =>
  s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

export default function Catalogue({ data }: { data: Gamme[] }) {
  const [active, setActive] = useState<string>("toutes");
  const [query, setQuery] = useState("");
  const [lightbox, setLightbox] = useState<number | null>(null);

  const total = useMemo(() => data.reduce((s, g) => s + g.count, 0), [data]);

  const visibles = useMemo(() => {
    const q = sansAccents(query.trim());
    const base =
      active === "toutes" ? data.flatMap((g) => g.produits.map((p) => ({ ...p, gamme: g }))) : data.filter((g) => g.id === active).flatMap((g) => g.produits.map((p) => ({ ...p, gamme: g })));
    if (!q) return base;
    return base.filter(
      (p) => sansAccents(p.name).includes(q) || sansAccents(p.gamme.name).includes(q) || sansAccents(p.file).includes(q)
    );
  }, [data, active, query]);

  const select = useCallback((id: string) => {
    setActive(id);
    setLightbox(null);
    setTimeout(() => {
      document.getElementById("grille")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }, []);

  const suivant = useCallback(() => {
    setLightbox((i) => (i === null ? null : (i + 1) % visibles.length));
  }, [visibles.length]);

  const precedent = useCallback(() => {
    setLightbox((i) => (i === null ? null : (i - 1 + visibles.length) % visibles.length));
  }, [visibles.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") suivant();
      if (e.key === "ArrowLeft") precedent();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [lightbox, suivant, precedent]);

  const courant = lightbox !== null ? visibles[lightbox] : null;

  return (
    <div className="min-h-screen flex flex-col bg-[#0b0d0c] text-[#ece7dd] selection:bg-[#d4af37] selection:text-black">
      {/* ===== En-tête ===== */}
      <header className="sticky top-0 z-40 border-b border-[#d4af37]/20 bg-[#0b0d0c]/85 backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="grid size-9 shrink-0 place-items-center rounded-full border border-[#d4af37]/50 bg-[#14291f] text-[#d4af37]">
              <Crown className="size-4" aria-hidden />
            </span>
            <div className="min-w-0 leading-tight">
              <p className="font-serif font-bold tracking-wide text-sm sm:text-base truncate">
                XALEY <span className="text-[#d4af37]">WALIENS</span>
              </p>
              <p className="hidden sm:block text-[10px] uppercase tracking-[0.2em] text-[#9aa89f]">
                Catalogue officiel des produits
              </p>
            </div>
          </div>
          <nav aria-label="Navigation principale" className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm">
            <button onClick={() => select("toutes")} className="px-3 py-2 rounded-full text-[#c9c3b6] hover:text-[#d4af37] hover:bg-[#14291f] transition-colors">
              Gammes
            </button>
            <button
              onClick={() => document.getElementById("grille")?.scrollIntoView({ behavior: "smooth" })}
              className="px-4 py-2 rounded-full bg-[#d4af37] text-black font-semibold hover:bg-[#e6c25a] transition-colors"
            >
              {total} visuels
            </button>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        {/* ===== Héros ===== */}
        <section className="relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_-10%,#14291f_0%,transparent_60%)]" />
          <div aria-hidden className="pointer-events-none absolute -top-24 left-1/2 h-72 w-[36rem] -translate-x-1/2 rounded-full bg-[#d4af37]/10 blur-3xl" />
          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 pt-16 pb-12 text-center">
            <p className="mb-6 inline-block rounded-full border border-[#d4af37]/40 px-4 py-1.5 text-[10px] sm:text-xs uppercase tracking-[0.35em] text-[#d4af37]">
              Entreprise XALEY WALIENS · Serigne Fallou Ndiaye
            </p>
            <h1 className="font-serif text-5xl sm:text-7xl font-black leading-[1.02]">
              XALEY <span className="bg-gradient-to-r from-[#f0d98c] via-[#d4af37] to-[#a8842a] bg-clip-text text-transparent">WALIENS</span>
            </h1>
            <p className="mt-5 font-serif italic text-lg sm:text-xl text-[#c9c3b6] max-w-2xl mx-auto">
              « Le Premier et le Meilleur est{" "}
              <span className="text-[#d4af37] not-italic font-semibold">Waly Ballago Seck</span> »
            </p>
            <div className="mx-auto my-8 h-px w-24 bg-[#d4af37]" aria-hidden />
            <p className="mx-auto max-w-2xl text-sm sm:text-base leading-relaxed text-[#9aa89f]">
              Une collection d&apos;exception où chaque produit est sublimé par des étiquettes
              luxueuses, une typographie dorée gravée et un rendu commercial d&apos;une pureté
              rare — sur fond de soie émeraude, de marbre blanc et de bokeh doré.
            </p>
            <dl className="mt-10 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl mx-auto">
              {[
                [String(total), "visuels produits"],
                [String(data.length), "gammes d'exception"],
                ["×99", "visuels par gamme"],
              ].map(([v, l]) => (
                <div key={l} className="rounded-2xl border border-[#d4af37]/20 bg-[#10140f] px-2 py-4 sm:py-5">
                  <dt className="sr-only">{l}</dt>
                  <dd className="font-serif text-2xl sm:text-3xl font-bold text-[#d4af37]">{v}</dd>
                  <dd className="mt-1 text-[10px] sm:text-xs uppercase tracking-wider text-[#9aa89f]">{l}</dd>
                </div>
              ))}
            </dl>
            <button
              onClick={() => document.getElementById("gammes")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-10 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 px-6 py-3 text-sm text-[#d4af37] hover:bg-[#14291f] transition-colors"
            >
              Explorer les gammes <ArrowDown className="size-4" aria-hidden />
            </button>
          </div>
        </section>

        {/* ===== Aperçu des gammes ===== */}
        <section id="gammes" className="mx-auto max-w-7xl px-4 sm:px-6 py-10 scroll-mt-20">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold">
            Les <span className="text-[#d4af37]">{data.length} gammes</span> du catalogue
          </h2>
          <p className="mt-2 text-sm text-[#9aa89f] max-w-2xl">
            Des eaux MAOUN aux thés de prestige, de la pâtisserie fine à la poissonnerie
            d&apos;exception Papa Thione — chaque gamme déploie 99 visuels maîtres.
            Touchez une gamme pour filtrer la galerie.
          </p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {data.map((g) => {
              const Icon = ICONS[g.icon] ?? Sparkles;
              const cover = g.produits[0]?.url;
              const on = active === g.id;
              return (
                <button
                  key={g.id}
                  onClick={() => select(g.id)}
                  aria-pressed={on}
                  className={`group text-left rounded-2xl border overflow-hidden transition-all ${
                    on ? "border-[#d4af37] ring-1 ring-[#d4af37]/60" : "border-[#d4af37]/15 hover:border-[#d4af37]/50"
                  } bg-[#10140f]`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#07110c]">
                    {cover && (
                      <img
                        src={cover} alt={`Gamme ${g.name}`} loading="lazy"
                        className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                    <span className="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-[9px] uppercase tracking-widest text-[#d4af37]">
                      {g.tag}
                    </span>
                  </div>
                  <div className="p-3">
                    <p className="flex items-center gap-1.5 text-xs font-medium leading-snug line-clamp-2">
                      <Icon className="size-3.5 shrink-0 text-[#d4af37]" aria-hidden /> {g.name}
                    </p>
                    <p className="mt-1.5 text-[10px] uppercase tracking-wider text-[#9aa89f]">{g.count} visuels</p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* ===== Barre de filtre ===== */}
        <section className="sticky top-16 z-30 border-y border-[#d4af37]/15 bg-[#0b0d0c]/90 backdrop-blur-md">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#9aa89f]" aria-hidden />
                <Input
                  value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher un produit, une marque, une gamme…"
                  aria-label="Rechercher un produit"
                  className="pl-9 h-11 rounded-full border-[#d4af37]/25 bg-[#10140f] text-sm placeholder:text-[#6f7a72] focus-visible:ring-[#d4af37]/50"
                />
              </div>
              <p className="text-xs text-[#9aa89f] sm:text-right shrink-0" role="status" aria-live="polite">
                {visibles.length} visuel{visibles.length > 1 ? "s" : ""} affiché{visibles.length > 1 ? "s" : ""}
              </p>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mb-1 [scrollbar-width:thin]">
              <Chip on={active === "toutes"} onClick={() => setActive("toutes")} label="Toutes les gammes" count={total} />
              {data.map((g) => (
                <Chip key={g.id} on={active === g.id} onClick={() => setActive(g.id)} label={g.name} count={g.count} />
              ))}
            </div>
          </div>
        </section>

        {/* ===== Grille des produits ===== */}
        <section id="grille" className="mx-auto max-w-7xl px-4 sm:px-6 py-8 scroll-mt-36">
          {visibles.length === 0 ? (
            <div className="py-20 text-center">
              <p className="font-serif text-2xl text-[#c9c3b6]">Aucun visuel ne correspond</p>
              <p className="mt-2 text-sm text-[#9aa89f]">Essayez « Maoun », « Sabar », « Or » ou choisissez une gamme.</p>
            </div>
          ) : (
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {visibles.map((p, i) => (
                <li key={`${p.gamme.id}-${p.file}`}>
                  <button
                    onClick={() => setLightbox(i)}
                    className="group w-full text-left rounded-2xl border border-[#d4af37]/15 bg-[#10140f] overflow-hidden transition-all hover:border-[#d4af37]/60 hover:shadow-[0_8px_30px_rgba(212,175,55,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d4af37]"
                    aria-label={`Agrandir ${p.name} — gamme ${p.gamme.name}`}
                  >
                    <div className="relative aspect-square bg-[#07110c] overflow-hidden">
                      <img
                        src={p.url} alt={p.name} loading="lazy"
                        className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                      <span className="absolute left-2 top-2 rounded-md bg-black/70 px-1.5 py-0.5 font-mono text-[10px] text-[#d4af37]">
                        #{p.num}
                      </span>
                    </div>
                    <div className="p-3">
                      <p className="text-xs sm:text-[13px] font-medium leading-snug line-clamp-2 group-hover:text-[#d4af37] transition-colors">
                        {p.name}
                      </p>
                      <p className="mt-1 text-[10px] uppercase tracking-wider text-[#9aa89f] truncate">{p.gamme.name}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      {/* ===== Pied de page ===== */}
      <footer className="mt-auto border-t border-[#d4af37]/20 bg-[#090b0a]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 text-center space-y-4 pb-[max(2.5rem,env(safe-area-inset-bottom))]">
          <p className="font-serif text-xl font-bold">XALEY <span className="text-[#d4af37]">WALIENS</span></p>
          <p className="font-serif italic text-sm text-[#d4af37]">
            « Le Premier et le Meilleur est Waly Ballago Seck »
          </p>
          <p className="text-xs text-[#9aa89f] max-w-xl mx-auto leading-relaxed">
            Entreprise XALEY WALIENS de Serigne Fallou Ndiaye — Recommandation de Son Altesse
            Pape Ndiaw Ndiaye. {total} visuels photoréalistes en {data.length} gammes d&apos;exception.
          </p>
          <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[10px] text-[#6f7a72]">
            {SITES.map((s) => <li key={s}>{s}</li>)}
          </ul>
        </div>
      </footer>

      {/* ===== Visionneuse ===== */}
      <Dialog open={lightbox !== null} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-4xl border-[#d4af37]/30 bg-[#0b0d0c] p-3 sm:p-4 gap-0 [&>button]:hidden">
          {courant && (
            <div>
              <div className="relative overflow-hidden rounded-xl bg-[#07110c]">
                <img src={courant.url} alt={courant.name} className="mx-auto max-h-[70vh] w-auto object-contain" />
                <span className="absolute left-3 top-3 rounded-md bg-black/70 px-2 py-0.5 font-mono text-xs text-[#d4af37]">
                  #{courant.num} · {lightbox! + 1}/{visibles.length}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <button
                  onClick={precedent} aria-label="Visuel précédent"
                  className="grid size-10 shrink-0 place-items-center rounded-full border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#14291f] transition-colors"
                >
                  <ChevronLeft className="size-5" aria-hidden />
                </button>
                <div className="min-w-0 text-center">
                  <DialogTitle className="font-serif text-base sm:text-lg font-semibold text-[#ece7dd]">
                    {courant.name}
                  </DialogTitle>
                  <p className="mt-0.5 text-[11px] uppercase tracking-wider text-[#9aa89f] truncate">
                    {courant.gamme.tag} — {courant.gamme.name}
                  </p>
                </div>
                <button
                  onClick={suivant} aria-label="Visuel suivant"
                  className="grid size-10 shrink-0 place-items-center rounded-full border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#14291f] transition-colors"
                >
                  <ChevronRight className="size-5" aria-hidden />
                </button>
              </div>
              <button
                onClick={() => setLightbox(null)} aria-label="Fermer la visionneuse"
                className="absolute right-3 top-3 grid size-9 place-items-center rounded-full bg-black/70 border border-[#d4af37]/30 text-[#ece7dd] hover:text-[#d4af37] transition-colors"
              >
                <X className="size-4" aria-hidden />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Chip({ on, onClick, label, count }: { on: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button
      onClick={onClick} aria-pressed={on}
      className={`shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
        on
          ? "border-[#d4af37] bg-[#d4af37] text-black font-semibold"
          : "border-[#d4af37]/25 text-[#c9c3b6] hover:border-[#d4af37]/60 hover:text-[#d4af37]"
      }`}
    >
      {label} <span className={on ? "text-black/70" : "text-[#6f7a72]"}>· {count}</span>
    </button>
  );
}
